import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { FirebaseApp, FirebaseOptions, getApp, getApps, initializeApp } from 'firebase/app';
import {
  Auth,
  getAuth,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  UserCredential,
} from 'firebase/auth';

import { FIREBASE_CONFIG } from '../../../core/config/firebase-config.token';

export interface FirebaseSignInResult {
  uid: string;
  email?: string;
  displayName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FirebaseAuthService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly firebaseConfig = inject(FIREBASE_CONFIG);
  private authClient: Auth | null = null;

  public async signInWithEmailPassword(
    email: string,
    password: string,
  ): Promise<FirebaseSignInResult> {
    const auth = this.getAuthClient();
    const credential = await signInWithEmailAndPassword(auth, email, password);

    return this.toSignInResult(credential);
  }

  public async signInWithGoogle(): Promise<FirebaseSignInResult> {
    const auth = this.getAuthClient();
    const provider = new GoogleAuthProvider();
    const credential = await signInWithPopup(auth, provider);

    return this.toSignInResult(credential);
  }

  public async signOut(): Promise<void> {
    const auth = this.getAuthClient();
    await signOut(auth);
  }

  private getAuthClient(): Auth {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('Firebase sign-in is only available in browser context.');
    }

    if (!this.isFirebaseConfigPresent(this.firebaseConfig)) {
      throw new Error('Firebase configuration is missing in environment settings.');
    }

    if (!this.authClient) {
      const app = this.getOrInitFirebaseApp(this.firebaseConfig);
      this.authClient = getAuth(app);
    }

    return this.authClient;
  }

  private getOrInitFirebaseApp(firebaseConfig: FirebaseOptions): FirebaseApp {
    if (getApps().length > 0) {
      return getApp();
    }

    return initializeApp(firebaseConfig);
  }

  private isFirebaseConfigPresent(firebaseConfig: FirebaseOptions): boolean {
    return Boolean(firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId);
  }

  private toSignInResult(credential: UserCredential): FirebaseSignInResult {
    return {
      uid: credential.user.uid,
      email: credential.user.email ?? undefined,
      displayName: credential.user.displayName ?? undefined,
    };
  }
}
